import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { Typography, Card, Space, Tag, Divider, Button, Skeleton } from 'antd';
import { ArrowLeftOutlined, CalendarOutlined } from '@ant-design/icons';
import PageHeader from '../Layout/PageHeader';
import { tns } from '../../i18n/common';

const { Title, Paragraph, Text } = Typography;

const ReleaseDetail = () => {
    const { slug } = useParams();
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(false);
    const [locale, setLocale] = useState(localStorage.getItem('locale') || 'en');

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`/v1/releases/${slug}`, { params: { locale } });
                setItem(res.data?.data || null);
            } catch (e) {
                console.error('Failed to load release', e);
            } finally {
                setLoading(false);
            }
        };
        if (slug) {
            load();
        }
    }, [slug, locale]);

    // Listen for locale changes
    useEffect(() => {
        const handleLocaleChange = () => {
            const newLocale = localStorage.getItem('locale') || 'en';
            setLocale(newLocale);
        };
        window.addEventListener('locale-changed', handleLocaleChange);
        return () => window.removeEventListener('locale-changed', handleLocaleChange);
    }, []);

    if (loading) {
        return (
            <div style={{
                padding: '40px 24px',
                backgroundColor: '#fafafa',
                minHeight: '100vh'
            }}>
                <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                    <Skeleton active paragraph={{ rows: 8 }} />
                </div>
            </div>
        );
    }

    if (!item) {
        return (
            <div style={{
                padding: '40px 24px',
                backgroundColor: '#fafafa',
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <Card style={{ textAlign: 'center', maxWidth: 400 }}>
                    <Title level={3}>{tns('releases', 'releaseNotFound')}</Title>
                    <Text type="secondary">{tns('releases', 'releaseNotFoundDescription')}</Text>
                    <div style={{ marginTop: 16 }}>
                        <Link to="/releases">
                            <Button type="primary" icon={<ArrowLeftOutlined />}>
                                {tns('releases', 'backToReleases')}
                            </Button>
                        </Link>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div style={{
            backgroundColor: '#fafafa',
            minHeight: '100vh'
        }}>
            <PageHeader
                backButton={{
                    to: "/releases",
                    text: tns('releases', 'backToReleases')
                }}
                title={item.title || item.slug}
                subtitle={null}
            >
                <Space size="small">
                    <Tag color="blue" style={{ fontSize: 12 }}>
                        {tns('releases', 'version')} {item.version || '—'}
                    </Tag>
                    {item.published_at && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            <CalendarOutlined style={{ marginRight: 4 }} />
                            {tns('releases', 'publishedOn')} {new Date(item.published_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })}
                        </Text>
                    )}
                </Space>
            </PageHeader>

            {/* Content Section */}
            <div style={{ padding: '40px 24px' }}>
                <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 32 }}>
                        <style>{`
                            .release-html ul { list-style: disc; margin: 0 0 8px 16px; padding-left: 16px; }
                            .release-html ol { list-style: decimal; margin: 0 0 8px 16px; padding-left: 16px; }
                            .release-html li { margin-bottom: 6px; }
                            .release-html p { margin: 0 0 12px; }
                        `}</style>
                        {/* Hero Image */}
                        <Card
                            style={{
                                padding: 0,
                                overflow: 'hidden',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }}
                            styles={{ body: { padding: 0 } }}
                        >
                            <img
                                src={item.hero_image_url || '/images/placeholder/release-placeholder.jpg'}
                                alt={item.hero_image_alt || item.title || ''}
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    display: 'block'
                                }}
                            />
                        </Card>

                        {/* Body Content */}
                        {item.body && (
                            <Card style={{
                                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                border: '1px solid #f0f0f0'
                            }}>
                                <div
                                    style={{
                                        fontSize: 16,
                                        lineHeight: 1.8,
                                        color: '#333'
                                    }}
                                    className="release-html"
                                    dangerouslySetInnerHTML={{ __html: item.body }}
                                />
                            </Card>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReleaseDetail;
